import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProjectPropertiesMenuItem,
  ProjectPropertiesMenuItemPrefix,
} from '@/features/projects/project-details/components/layouts/project-properties-menu-layout';
import { useUpdateProjectDates } from '@/features/projects/project-details/hooks/use-update-project-dates';
import { CustomDatePickerDialog } from '@/shared/components/ui/custom-date-picker-dialog';
import { Project } from '@/shared/types/api';
import { formatDate } from '@/shared/utils/format';

export const TimelineItem = ({ project }: { project: Project }) => {
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const [startDate, setStartDate] = useState<Date | undefined>(
    project.start_date ? new Date(project.start_date) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    project.end_date ? new Date(project.end_date) : undefined,
  );
  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);

  const mutation = useUpdateProjectDates();

  useEffect(() => {
    setStartDate(project.start_date ? new Date(project.start_date) : undefined);
    setEndDate(project.end_date ? new Date(project.end_date) : undefined);
  }, [project.id, project.start_date, project.end_date]);

  const handleDateRangeChange = (
    newStartDate: Date | undefined,
    newEndDate: Date | undefined,
  ) => {
    // Always update local state
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    // Handle start date change (including clearing)
    if (newStartDate !== startDate) {
      mutation.mutate({
        projectId: project.id,
        startDate: newStartDate ?? null,
      });
    }

    // Handle end date change (including clearing)
    if (newEndDate !== endDate) {
      mutation.mutate({
        projectId: project.id,
        endDate: newEndDate ?? null,
      });
    }
  };

  const progress = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const now = new Date();
    const totalDays = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    const daysElapsed = Math.ceil(
      (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    return Math.max(0, Math.min(100, (daysElapsed / totalDays) * 100));
  }, [startDate, endDate]);

  return (
    <ProjectPropertiesMenuItem
      isEditable
      onEdit={() => setIsDateRangeDialogOpen(true)}
      aspectRatio="170/216"
    >
      <div className="flex w-full flex-1 flex-col items-center">
        <ProjectPropertiesMenuItemPrefix className="mt-5">
          {t('project_timeline')}
        </ProjectPropertiesMenuItemPrefix>
        <div className="flex w-full flex-1 flex-col items-center justify-center">
          <SemiCircularProgressBar progress={progress} />
        </div>
        <div className="mb-5 flex w-full flex-col items-center gap-1">
          <CustomDatePickerDialog
            isOpen={isDateRangeDialogOpen}
            onOpenChange={setIsDateRangeDialogOpen}
            startDate={startDate}
            endDate={endDate}
            onDateRangeSelect={handleDateRangeChange}
            title={t('select_time_period')}
          >
            <div className="flex flex-col gap-1">
              <div className="flex w-fit flex-none flex-col gap-2">
                <div className="flex cursor-pointer items-center gap-1">
                  <span className="w-[35px] text-[12px] leading-[16px] text-custom-grey">
                    {t('from_date')}
                  </span>
                  <span className="text-[12px] leading-[16px] text-white">
                    {startDate
                      ? formatDate({
                          date: new Date(startDate),
                          formatType: 'digits',
                        })
                      : t('select_date')}
                  </span>
                </div>
              </div>

              <div className="flex w-fit flex-none flex-col gap-2">
                <div className="flex cursor-pointer items-center gap-1">
                  <span className="w-[35px] text-[12px] leading-[16px] text-custom-grey">
                    {t('to_date')}
                  </span>
                  <span className="text-[12px] leading-[16px] text-white">
                    {endDate
                      ? formatDate({
                          date: new Date(endDate),
                          formatType: 'digits',
                        })
                      : t('select_date')}
                  </span>
                </div>
              </div>
            </div>
          </CustomDatePickerDialog>
        </div>
      </div>
    </ProjectPropertiesMenuItem>
  );
};

const SemiCircularProgressBar = ({ progress }: { progress: number }) => {
  const size = 92;
  const strokeWidth = 16;
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius;
  const strokeDasharray = circumference;
  const cappedProgress = Math.max(0, Math.min(100, progress));
  const strokeDashoffset =
    circumference - (cappedProgress / 100) * circumference;

  return (
    <div className="relative flex h-[46px] w-[92px] items-center justify-center">
      <svg
        width={size}
        height={size / 2 + strokeWidth / 2}
        viewBox={`0 0 ${size} ${size / 2 + strokeWidth / 2}`}
        className="overflow-visible"
      >
        {/* Background arc (empty portion) */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke="rgba(255, 255, 255, 0.15)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Progress arc (filled portion) */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke="#897ABA"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-300 ease-in-out"
        />
      </svg>
    </div>
  );
};
